'use client';

import { c as _c } from "react/compiler-runtime";
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/common/button';
import { Checkbox } from '@/components/common/checkbox';
import { Label } from '@/components/common/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/select';
import { Switch } from '@/components/common/switch';
import LocationForm from '@/components/features/Geolocation/LocationForm';
import type { Geolocation } from '@/lib/server/geolocation/types';
import { normalizeRegionName } from '@/lib/utils/locationSearch';
type Region = {
  _id: string;
  name: string | null;
};
type Amenity = {
  _id: string;
  name: string | null;
};
type FilterSectionProps = {
  selectedRegions: string[];
  selectedAmenities: string[];
  searchLocation: Geolocation | null;
  searchRadius: number;
  regions?: Region[] | null;
  amenities?: Amenity[] | null;
  showMap?: boolean;
  onRegionToggle: (region: string) => void;
  onAmenityToggle: (amenityId: string) => void;
  onSearchLocationChange: (location: Geolocation | null) => void;
  onSearchRadiusChange: (radius: number) => void;
  onShowMapChange?: (show: boolean) => void;
  onClearFilters: () => void;
};
const RADIUS_OPTIONS = [10, 20, 30, 50];
export type FilterSectionContentProps = FilterSectionProps & {
  inDrawer?: boolean;
};
export function FilterSectionContent(t0) {
  const $ = _c(54);
  const {
    selectedRegions,
    selectedAmenities,
    searchLocation,
    searchRadius,
    regions: t1,
    amenities: t2,
    showMap: t3,
    onRegionToggle,
    onAmenityToggle,
    onSearchLocationChange,
    onSearchRadiusChange,
    onShowMapChange,
    onClearFilters,
    inDrawer: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const regions = t5;
  let t6;
  if ($[2] !== t2) {
    t6 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const amenities = t6;
  const showMap = t3 === undefined ? false : t3;
  const inDrawer = t4 === undefined ? false : t4;
  const [inputValue, setInputValue] = useState(searchLocation?.formattedAddress || "");
  let t7;
  if ($[4] !== searchLocation?.formattedAddress) {
    t7 = () => {
      setInputValue(searchLocation?.formattedAddress || "");
    };
    $[4] = searchLocation?.formattedAddress;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== searchLocation) {
    t8 = [searchLocation];
    $[6] = searchLocation;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Label className="md:text-lg font-bold text-primary-blue">Location</Label>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      variant: "ghost",
      size: "sm",
      iconOnly: true,
      className: "h-auto p-0 shrink-0"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== inputValue || $[11] !== onSearchLocationChange) {
    t11 = <LocationForm placeholder="City, Town, Postcode" value={inputValue} minQueryLength={2} updateUrlParams={false} scrollToResults={false} onLocationChange={onSearchLocationChange} showGeolocationButton={true} showErrorMessage={false} className="rounded-[10px] bg-white flex flex-row px-[19px] justify-between" inputClassName="pr-4" geolocationButtonProps={t10} />;
    $[10] = inputValue;
    $[11] = onSearchLocationChange;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== onShowMapChange || $[14] !== showMap) {
    t12 = onShowMapChange && <div className="flex flex-col gap-[10px]"><Label className="md:text-lg font-bold text-primary-blue sr-only">Show Map</Label><div className="flex items-center gap-2"><Switch id="show-map-toggle" checked={showMap} onCheckedChange={onShowMapChange} aria-label="Show map" /><Label htmlFor="show-map-toggle" className="cursor-pointer text-lg text-primary-blue leading-[26px]">Map</Label></div></div>;
    $[13] = onShowMapChange;
    $[14] = showMap;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t11 || $[17] !== t12) {
    t13 = <div className="flex flex-col gap-2.5 w-full md:w-[327.5px] justify-between">{t9}{t11}{t12}</div>;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label className="md:text-lg font-bold text-primary-blue">Search radius</Label>;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== searchRadius) {
    t15 = searchRadius.toString();
    $[20] = searchRadius;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== onSearchRadiusChange) {
    t16 = value => onSearchRadiusChange(Number(value));
    $[22] = onSearchRadiusChange;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SelectTrigger aria-label="Search radius" className="h-[54px]! border-none w-full rounded-[10px] bg-white px-[19px] text-lg text-blue-300 leading-[26px]"><SelectValue placeholder="30 miles" /></SelectTrigger>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SelectContent>{RADIUS_OPTIONS.map(_temp)}</SelectContent>;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t15 || $[27] !== t16) {
    t19 = <Select value={t15} onValueChange={t16}>{t17}{t18}</Select>;
    $[26] = t15;
    $[27] = t16;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== inDrawer || $[30] !== onClearFilters) {
    t20 = !inDrawer && <Button type="button" variant="link" className="self-end" onClick={() => {
      onClearFilters();
      setInputValue("");
    }}><X className="size-4" />Clear all filters</Button>;
    $[29] = inDrawer;
    $[30] = onClearFilters;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t19 || $[33] !== t20) {
    t21 = <div className="flex flex-col gap-[10px] w-full md:w-[327.5px] justify-between">{t14}{t19}{t20}</div>;
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Label className="md:text-lg font-bold text-primary-blue">Select Region</Label>;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== onRegionToggle || $[37] !== regions || $[38] !== selectedRegions) {
    t23 = regions && regions.length > 0 ? regions.map(region => {
      const regionName = normalizeRegionName(region.name || "");
      return <div key={region._id} className="flex items-center gap-2"><Checkbox id={`region-${region._id}`} checked={selectedRegions.includes(regionName)} onCheckedChange={() => onRegionToggle(regionName)} className="size-[19px] bg-white border-none " aria-label={regionName} /><Label htmlFor={`region-${region._id}`} className="cursor-pointer text-lg text-primary-blue leading-[26px]">{regionName}</Label></div>;
    }) : <div className="text-sm text-primary-blue">No regions available</div>;
    $[36] = onRegionToggle;
    $[37] = regions;
    $[38] = selectedRegions;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t23) {
    t24 = <div className="flex flex-col gap-[10px]">{t22}<div className="flex flex-col gap-[6px]">{t23}</div></div>;
    $[40] = t23;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Label className="md:text-lg font-bold text-primary-blue">Amenities</Label>;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== amenities || $[44] !== onAmenityToggle || $[45] !== selectedAmenities) {
    t26 = amenities && amenities.length > 0 ? amenities.map(amenity => {
      const amenityName = amenity.name || "";
      return <div key={amenity._id} className="flex items-center gap-2"><Checkbox id={`amenity-${amenity._id}`} checked={selectedAmenities.includes(amenity._id)} onCheckedChange={() => onAmenityToggle(amenity._id)} className="size-[19px] bg-white border-none" aria-label={amenityName} /><Label htmlFor={`amenity-${amenity._id}`} className="cursor-pointer text-lg text-primary-blue leading-[26px]">{amenityName}</Label></div>;
    }) : <div className="text-sm text-primary-blue">No amenities available</div>;
    $[43] = amenities;
    $[44] = onAmenityToggle;
    $[45] = selectedAmenities;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t26) {
    t27 = <div className="flex flex-col gap-[10px]">{t25}<div className="flex flex-col gap-[6px]">{t26}</div></div>;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== t13 || $[50] !== t21 || $[51] !== t24 || $[52] !== t27) {
    t28 = <div className="flex flex-col gap-4 md:gap-22 md:flex-row md:items-stretch">{t13}{t21}{t24}{t27}</div>;
    $[49] = t13;
    $[50] = t21;
    $[51] = t24;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  return t28;
}
function _temp(radius) {
  return <SelectItem key={radius} value={radius.toString()}>{radius} miles</SelectItem>;
}
export default function FilterSection(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <div className="bg-primary-green flex flex-col gap-5 py-20"><div className="mx-auto max-w-7xl px-5"><FilterSectionContent {...props} /></div></div>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}